import { CognitoUserPool, CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import env from "@/configuration/env";

// vars
var pool;
var refreshInterval;

// internal functions
const shouldRefresh = state => {
  if (!state.user || !state.session) return;
  var now = new Date().getTime() / 1000;
  var exp = state.session.getIdToken().payload.exp;
  var ttl = exp - now;
  return ttl < 1500;
};

export default {
  strict: true,
  namespaced: true,
  state: {
    isInitialized: false,
    user: null,
    session: null,
  },
  getters: {
    isInitialized: state => state.isInitialized,
    isAuthenticated: state => !!state.user,
    userId: state => (state.session ? state.session.getIdToken().payload.sub : null),
    idToken: state => (state.session ? state.session.getIdToken().getJwtToken() : null),
  },
  mutations: {
    initialize: state => (state.isInitialized = true),
    signIn: (state, payload) => {
      state.user = payload.user;
      state.session = payload.session;
    },
    signOut: state => {
      state.user = null;
      state.session = null;
    },
  },
  actions: {
    initialize: context =>
      new Promise(resolve => {
        // pool
        pool = new CognitoUserPool({
          UserPoolId: env.cognito_user_pool_id,
          ClientId: env.cognito_user_pool_client_id,
        });

        // user
        const user = pool.getCurrentUser();
        if (!user) {
          context.commit("initialize");
          resolve({ success: true });
          return;
        }

        // session
        user.getSession((err, session) => {
          if (session) {
            context.commit("signIn", { user, session });
          }

          context.commit("initialize");
          resolve({ success: true });
        });

        // refresh
        refreshInterval =
          refreshInterval ||
          setInterval(() => {
            if (shouldRefresh(context.state)) {
              var refreshToken = context.state.session.getRefreshToken();
              user.refreshSession(refreshToken, (err, newSession) => {
                context.commit("signIn", { user, session: newSession });
              });
            }
          }, 60 * 1000);
      }),
    signIn: (context, payload) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: payload.username,
        Password: payload.password,
      });

      const user = new CognitoUser({
        Pool: pool,
        Username: payload.username,
      });

      return new Promise((resolve, reject) =>
        user.authenticateUser(authenticationDetails, {
          onFailure: err => reject(err),
          onSuccess: session => {
            context.commit("signIn", { user, session });
            resolve({ success: true });
          },
          newPasswordRequired: () => {
            resolve({ success: false, newPasswordRequired: true });
          },
        })
      );
    },
    completeNewPasswordChallenge: (context, payload) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: payload.username,
        Password: payload.password,
      });

      const user = new CognitoUser({
        Pool: pool,
        Username: payload.username,
      });

      return new Promise((resolve, reject) =>
        user.authenticateUser(authenticationDetails, {
          onFailure: err => reject(err),
          onSuccess: session => {
            context.commit("signIn", { user, session });
            resolve({ success: true });
          },
          newPasswordRequired: () => {
            user.completeNewPasswordChallenge(payload.newPassword, null, {
              onFailure: err => reject(err),
              onSuccess: session => {
                context.commit("signIn", { user, session });
                resolve({ success: true });
              },
            });
          },
        })
      );
    },
    signOut: context =>
      new Promise(resolve => {
        const user = context.state.user;
        if (user === null || (user && user.tokens === null)) {
          resolve({ success: true });
          return;
        }

        const signOutUser = new CognitoUser({
          Pool: pool,
          Username: user.username,
        });

        signOutUser.signOut();
        context.commit("signOut");
        resolve({ success: true });
      }),
  },
};
